// frontend/src/pages/OrdiniRicevuti.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import TornaAlMenu from "../components/TornaAlMenu";

const API_URL = process.env.REACT_APP_API_URL || "";

const APP_STATUS_FLOW = [
  { value: "INVIATO", label: "Ricevuto" },
  { value: "IN_PREPARAZIONE", label: "In preparazione" },
  { value: "PRONTO_RITIRO", label: "Pronto al ritiro" },
  { value: "RITIRATO", label: "Ritirato" },
];

// ————— Archiviazione soft in localStorage —————
const ARCHIVE_KEY = "archived_orders_ids";
function loadArchivedIds() {
  try {
    const raw = localStorage.getItem(ARCHIVE_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
}
function saveArchivedIds(setIds) {
  try {
    localStorage.setItem(ARCHIVE_KEY, JSON.stringify(Array.from(setIds)));
  } catch {}
}

export default function OrdiniRicevuti() {
  const nav = useNavigate();
  const { search, pathname } = useLocation();
  const params = new URLSearchParams(search);

  // seed da querystring (compat: ?q=, ?created_by=, ?status=, ?user_id=)
  const qSeed = params.get("q") || params.get("created_by") || "";
  const statusSeed = (params.get("status") || "").toUpperCase();
  const userIdParam = params.get("user_id"); // inoltrato al backend se presente
  const sourceParam = (params.get("source") || "").toLowerCase(); // app
  const appViewParam = (params.get("app_view") || "active").toLowerCase(); // active | history

  // nuovo: ?view=new | all
  const viewParam = (params.get("view") || "").toLowerCase(); // new, all
  const fromStorico = params.get("from") === "storico";
  const fromAppClienti = params.get("from") === "app-clienti";
  const mustReturnToAppManagement =
    fromAppClienti ||
    (sourceParam === "app" && appViewParam === "history") ||
    localStorage.getItem("dzr_return_to_app_management") === "1" ||
    pathname.startsWith("/gestione-app-clienti/ordini");

  const [loading, setLoading] = useState(true);
  const [ordini, setOrdini] = useState([]);
  const [status, setStatus] = useState("ALL");
  const [seeded, setSeeded] = useState(false);
  const [qInput, setQInput] = useState("");
  const [qDebounced, setQDebounced] = useState("");
  const now = new Date();
  const [historyMonth, setHistoryMonth] = useState(String(now.getMonth() + 1).padStart(2, "0"));
  const [historyYear, setHistoryYear] = useState(String(now.getFullYear()));
  const [err, setErr] = useState("");
  const [updatingAppStatusId, setUpdatingAppStatusId] = useState(null);

  // archiviazione soft
  const [archivedIds, setArchivedIds] = useState(() => loadArchivedIds());
  const [hideArchived, setHideArchived] = useState(true);

  const archiveOrder = (id) => {
    const s = new Set(archivedIds);
    s.add(Number(id));
    setArchivedIds(s);
    saveArchivedIds(s);
  };
  const unarchiveOrder = (id) => {
    const s = new Set(archivedIds);
    s.delete(Number(id));
    setArchivedIds(s);
    saveArchivedIds(s);
  };
  const isArchived = (id) => archivedIds.has(Number(id));

  // debounce della ricerca (400ms)
  useEffect(() => {
    const t = setTimeout(() => setQDebounced(qInput.trim()), 400);
    return () => clearTimeout(t);
  }, [qInput]);

  // --- mapping IT ---
  const statusCodeToIt = (code) => {
    const s = (code || "").toUpperCase();
    if (s === "DRAFT") return "Bozza";
    if (s === "SUBMITTED" || s === "INVIATO") return "Ricevuto";
    if (s === "APPROVED") return "Approvato";
    if (s === "FULFILLED") return "Evaso";
    if (s === "CANCELED") return "Annullato";
    return code || "";
  };
  const statusLabel = (o) => {
    if (sourceParam === "app" && ["SUBMITTED", "INVIATO"].includes(String(o.status || "").toUpperCase())) {
      return "Ricevuto";
    }
    return o.status_it || statusCodeToIt(o.status);
  };

  const formatDateIT = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleString("it-IT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // helpers scadenza
  const msPerDay = 1000 * 60 * 60 * 24;
  const parseDate = (s) => (s ? new Date(s) : null);
  const diffDays = (a, b) => Math.floor((a - b) / msPerDay);
  const getOverdueInfo = (o) => {
    if (o.is_paid) return { over: false, days: 0 };
    if (typeof o.is_overdue === "boolean") {
      return { over: o.is_overdue, days: Number(o.days_overdue || 0) };
    }
    const now = new Date();
    const due = parseDate(o.due_date);
    if (due) {
      const d = diffDays(now, due);
      return { over: d > 0, days: d > 0 ? d : 0 };
    }
    const created = parseDate(o.date);
    if (created) {
      const d = diffDays(now, created);
      return { over: d > 30, days: d > 30 ? d - 30 : 0 };
    }
    return { over: false, days: 0 };
  };

  // --- ruolo creatore ordine: TRUE se AGENTE ---
  const roleOfSender = (o) => {
    const direct =
      o.sender_role ||
      (o.sender && o.sender.role) ||
      o.creator_role ||
      o.sender_role_name ||
      "";
    if (direct) return String(direct).toUpperCase();
    if (typeof o.creato_da === "string") {
      const m = o.creato_da.match(/\(([^)]+)\)/);
      if (m && m[1]) return m[1].toUpperCase();
    }
    return "";
  };
  const isOrderFromAgent = (o) => roleOfSender(o) === "AGENTE";
  const isB2BOrder = (o) => ["AGENTE", "AZIENDA"].includes(roleOfSender(o));
  const b2bOriginLabel = (o) =>
    roleOfSender(o) === "AGENTE" ? "Ordine AGENTE" : "Ordine AZIENDA";

  // mappa valore select -> parametro backend
  const mapStatusParam = useCallback((s) => {
    const up = (s || "").toUpperCase();
    if (up === "SUBMITTED") return "submitted";
    if (up === "APPROVED") return "approved";
    if (up === "FULFILLED") return "fulfilled";
    if (up === "CANCELED") return "canceled";
    return "all";
  }, []);

const buildUrl = useCallback(() => {
    const sp = new URLSearchParams();
    sp.set("status", mapStatusParam(status)); // sempre esplicito
    if (qDebounced) sp.set("q", qDebounced);
    if (userIdParam) sp.set("user_id", userIdParam);
    if (sourceParam) sp.set("source", sourceParam);
    return `${API_URL}/api/ordini/ricevuti/?${sp.toString()}`;
  }, [status, qDebounced, userIdParam, sourceParam, mapStatusParam]);

  const fetchOrdini = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const token = localStorage.getItem("access");
      const res = await fetch(buildUrl(), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const text = await res.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        data = { __text: text };
      }
      if (!res.ok) {
        throw new Error(data?.detail || "Errore caricamento ordini");
      }
      const list = Array.isArray(data) ? data : data.results || [];
      setOrdini(list);
    } catch (e) {
      setErr(e.message || "Errore");
      setOrdini([]);
    } finally {
      setLoading(false);
    }
  }, [buildUrl]);

  // primo seed da URL
  useEffect(() => {
      if (qSeed) setQInput(qSeed);

      // decide status iniziale
      let nextStatus = "ALL";

      // priorità: ?view=new/all → override del seed "status"
      if (viewParam === "new") {
          nextStatus = "SUBMITTED";
      } else if (viewParam === "all") {
        nextStatus = "ALL";
      } else if (
        statusSeed &&
        ["ALL", "SUBMITTED", "APPROVED", "FULFILLED", "CANCELED"].includes(statusSeed)
      ) {
        nextStatus = statusSeed;
      }

      setStatus(nextStatus);
      setSeeded(true);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
// fetch iniziale e su variazioni filtri
  useEffect(() => {
    if (!seeded) return;
    fetchOrdini();
  }, [seeded, fetchOrdini]);
// filtro client-side: nascondi archiviati se richiesto
  const filtered = useMemo(() => {
    let list = [...ordini];

    if (sourceParam === "app") {
      list = appViewParam === "history"
        ? list.filter((o) => String(o.app_status || "").toUpperCase() === "RITIRATO")
        : list.filter((o) => String(o.app_status || "").toUpperCase() !== "RITIRATO");

      if (appViewParam === "history") {
        if (historyYear) {
          list = list.filter((o) => String(o.date || "").slice(0, 4) === historyYear);
        }
        if (historyMonth) {
          list = list.filter((o) => String(o.date || "").slice(5, 7) === historyMonth);
        }
      }
    }

    return hideArchived ? list.filter((o) => !isArchived(o.id)) : list;
  }, [ordini, hideArchived, archivedIds, sourceParam, appViewParam, historyMonth, historyYear]);

  const totals = useMemo(() => {
    const count = filtered.length;
    const sum = filtered.reduce((acc, o) => acc + Number(o.total || 0), 0);
    return { count, sum: sum.toFixed(2) };
  }, [filtered]);

  const statusBadgeClass = (o) => {
    const code = (o.status || "").toUpperCase();
    if (code === "DRAFT") return "bg-secondary";
    if (code === "SUBMITTED" || code === "INVIATO") return "bg-info text-dark";
    if (code === "APPROVED") return "bg-primary";
    if (code === "FULFILLED") return o.is_paid ? "bg-success" : "bg-warning text-dark";
    if (code === "CANCELED") return "bg-secondary";
    return "bg-secondary";
  };

  const updateAppStatus = async (orderId, nextStatus) => {
    setErr("");
    setUpdatingAppStatusId(orderId);
    try {
      const token = localStorage.getItem("access");
      const res = await fetch(`${API_URL}/api/ordini/${orderId}/app-status/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ app_status: nextStatus }),
      });

      const text = await res.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        data = { __text: text };
      }

      if (!res.ok) {
        throw new Error(data?.detail || "Errore aggiornamento stato app");
      }

      await fetchOrdini();
    } catch (e) {
      setErr(e.message || "Errore aggiornamento stato app");
    } finally {
      setUpdatingAppStatusId(null);
    }
  };

  const resetFiltri = () => {
    setQInput("");
    setStatus("ALL");
  };

  return (
    <div className="container mt-4">
      {mustReturnToAppManagement ? (
        <div className="mb-3">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => {
              localStorage.removeItem("dzr_return_to_app_management");
              nav("/gestione-app-clienti");
            }}
          >
            ⬅️ Torna a Gestione App Clienti
          </button>
        </div>
      ) : (
        <TornaAlMenu to="/" />
      )}
      {fromStorico && (
        <button className="btn btn-outline-secondary mb-3 ms-2" onClick={() => nav("/ordini/storico")}>
          ← Torna allo storico ordini
        </button>
      )}
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
        <h2 className="m-0">
          {sourceParam === "app"
            ? (appViewParam === "history" ? "📚 Storico Ordini App Clienti" : "📱 Ordini App Clienti")
            : "📦 Ordini ricevuti"}
        </h2>
        <div className="d-flex gap-2">
          <button className="btn btn-outline-primary" onClick={fetchOrdini}>
            🔄 Aggiorna
          </button>
        </div>
      </div>

      {(qSeed || userIdParam) && (
        <div className="alert alert-secondary py-2">
          Filtro iniziale:
          {qSeed && <> <b>{qSeed}</b></>}
          {userIdParam && <> agente <b>#{userIdParam}</b></>}
          <button className="btn btn-sm btn-link ms-2" onClick={() => nav("/ordini/ricevuti/?view=all")}>
            rimuovi
          </button>
        </div>
      )}

      <div className="card shadow-sm mb-3">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-md-4">
              <label className="form-label mb-1">Cerca</label>
              <input
                className="form-control"
                placeholder="🔎 ID, cliente, creato da, metodo, stato…"
                value={qInput}
                onChange={(e) => setQInput(e.target.value)}
              />
            </div>
              {viewParam === "new" ? (
                <div className="col-md-5">
                  <label className="form-label mb-1">Vista</label>
                  <div className="form-control bg-light">Nuovi da gestire</div>
                </div>
              ) : (
                <>
            {sourceParam === "app" && appViewParam === "history" ? (
              <>
                <div className="col-md-3">
                  <label className="form-label mb-1">Mese</label>
                  <select
                    className="form-select"
                    value={historyMonth}
                    onChange={(e) => setHistoryMonth(e.target.value)}
                  >
                    <option value="">Tutti i mesi</option>
                    <option value="01">Gennaio</option>
                    <option value="02">Febbraio</option>
                    <option value="03">Marzo</option>
                    <option value="04">Aprile</option>
                    <option value="05">Maggio</option>
                    <option value="06">Giugno</option>
                    <option value="07">Luglio</option>
                    <option value="08">Agosto</option>
                    <option value="09">Settembre</option>
                    <option value="10">Ottobre</option>
                    <option value="11">Novembre</option>
                    <option value="12">Dicembre</option>
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="form-label mb-1">Anno</label>
                  <input
                    type="number"
                    className="form-control"
                    value={historyYear}
                    onChange={(e) => setHistoryYear(e.target.value)}
                    placeholder="YYYY"
                  />
                </div>
              </>
            ) : (
              <div className="col-md-3">
                <label className="form-label mb-1">Stato</label>
                <select
                  className="form-select"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <option value="ALL">Tutti</option>
                  <option value="SUBMITTED">Inviati</option>
                  <option value="APPROVED">Approvati</option>
                  <option value="CANCELED">Annullati</option>
                </select>
              </div>
            )}

            <div className="col-md-3 d-flex align-items-center gap-2">
              <div className="form-check">
                <input
                  id="hideArchived"
                  className="form-check-input"
                  type="checkbox"
                  checked={hideArchived}
                  onChange={(e) => setHideArchived(e.target.checked)}
                />
                <label htmlFor="hideArchived" className="form-check-label">
                  Nascondi archiviati
                </label>
              </div>
              <button className="btn btn-outline-secondary" onClick={resetFiltri}>
                Reset
              </button>
            </div>
                </>
              )}

            <div className="col-md-2 text-md-end">
              <div className="d-inline-flex align-items-center gap-3 p-2 border rounded w-100 justify-content-between">
                <span className="text-muted">#</span>
                <strong>{totals.count}</strong>
                <span className="text-muted">Totale</span>
                <strong>€ {totals.sum}</strong>
              </div>
            </div>
          </div>
        </div>
      </div>

      {err && <div className="alert alert-danger">{err}</div>}
      {loading ? (
        <p className="text-muted">Caricamento…</p>
      ) : filtered.length === 0 ? (
        <div className="alert alert-info">Nessun ordine trovato.</div>
      ) : (
        <div className="table-responsive">
          <table className="table align-middle">
            <thead>
              <tr>
                <th style={{ width: 80 }}>ID</th>
                <th>Data</th>
                <th>Cliente</th>
                <th>Creato da</th>
                <th>Stato</th>
                <th className="text-end">Totale</th>
                <th className="text-end">Provvigione</th>
                <th style={{ width: 220 }}>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((o) => {
                const over = getOverdueInfo(o);
                const showCommission = isOrderFromAgent(o);
                const b2b = isB2BOrder(o);
                const archived = isArchived(o.id);
                return (
                  <tr key={o.id} className={archived ? "table-light" : ""}>
                    <td>#{o.id}</td>
                    <td>{formatDateIT(o.date)}</td>
                    <td>
                      <div>{o.per_nome || o.cliente || "-"}</div>
                      {b2b && (
                        <span className="badge bg-primary mt-1">
                          {b2bOriginLabel(o)}
                        </span>
                      )}
                    </td>
                    <td>{o.creato_da || "-"}</td>
                    <td>
                      <div className="d-flex flex-wrap gap-1">
                        <span className={`badge ${statusBadgeClass(o)}`}>{statusLabel(o)}</span>

                        {o.is_paid ? (
                          <span className="badge bg-success">Pagato</span>
                        ) : (
                          <span className="badge bg-warning text-dark">Non pagato</span>
                        )}

                        {!o.is_paid && over.over && (
                          <span className="badge bg-danger">
                            {over.days > 0 ? `Scaduto (+${over.days}g)` : "Over-30"}
                          </span>
                        )}
                      </div>

                      {sourceParam === "app" && appViewParam !== "history" && (
                        <div className="mt-2">
                          <div className="small text-muted mb-1">
                            Workflow app cliente
                          </div>

                          <div className="d-flex flex-wrap gap-1">
                            {APP_STATUS_FLOW.map((st) => {
                              const active =
                                (o.app_status || "INVIATO").toUpperCase() === st.value;

                              const busy = updatingAppStatusId === o.id;

                              return (
                                <button
                                  key={st.value}
                                  type="button"
                                  className={`btn btn-sm ${
                                    active
                                      ? "btn-success"
                                      : "btn-outline-secondary"
                                  }`}
                                  disabled={busy || active}
                                  onClick={() => updateAppStatus(o.id, st.value)}
                                >
                                  {busy ? "..." : st.label}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {sourceParam === "app" && appViewParam === "history" && (
                        <div className="mt-2">
                          <span className="badge bg-success">Ritirato</span>
                          {!o.is_paid && (
                            <span className="badge bg-danger ms-1">Anomalia: ritirato non pagato</span>
                          )}
                        </div>
                      )}
                    </td>

                    <td className="text-end">€ {Number(o.total || 0).toFixed(2)}</td>
                    <td className="text-end">
                      {b2b
                        ? `€ ${Number(o.agent_comp_amount || 0).toFixed(2)}`
                        : showCommission
                          ? `€ ${Number(o.commission_amount || 0).toFixed(2)}`
                          : "—"}
                    </td>
                    <td>
                      <div className="d-flex flex-wrap gap-2">
                        <button
                          className="btn btn-sm btn-outline-dark"
                          onClick={() => nav(sourceParam === "app" ? `/ordini-app/${o.id}` : `/ordini/${o.id}`)}
                          title="Apri dettaglio"
                        >
                          Apri
                        </button>

                        {!archived ? (
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => archiveOrder(o.id)}
                            title="Archivia (nascondi dalla lista se abilitato)"
                          >
                            Archivia
                          </button>
                        ) : (
                          <button
                            className="btn btn-sm btn-outline-warning"
                            onClick={() => unarchiveOrder(o.id)}
                            title="Ripristina (rimuovi dall'archivio)"
                          >
                            Ripristina
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
